import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { FulfillmentOrderLine, FulfillmentSupplierOption } from "../../types";
import { confirmationCategoryForLine } from "../../service/confirmationEditorView";
import { formatConfirmationAmount } from "../../service/confirmationDecimalDisplay";
import { buildConfirmationEntryChange, changedEntryFields, ENTRY_RESOURCE_TYPES } from "../../service/confirmationEditorEntry";
import { parseMiscEntry } from "../../service/confirmationMiscEntryService";
import type { EntryExistingDraft, EntryProvider, EntryParseResult } from "../../service/confirmationEditorEntry";
import { CONFIRMATION_ENTRY_IDENTITY_ERROR, reconcileConfirmationEntryIdentities, type ConfirmationEntryIdentity } from "../../service/confirmationEntryIdentity";
import { prepareExistingMiscEntry, parseExistingMiscEntry, parseSelectedMiscDraft } from "../../service/confirmationExistingMiscEntry";
import type { ExistingConfirmationEntry } from "../../service/confirmationExistingEntryShared";
import { FULFILLMENT_PRICING_UNIT_OPTIONS } from "../../config";
import { calculateFulfillmentLineAmount, pricingUnitTypeLabel, sumFulfillmentAmounts } from "../../service/fulfillmentOrderEditorService";
import { ConfirmationButton, ConfirmationInput, ConfirmationSheet } from "./primitives";
import { EntrySupplierPicker } from "./EntryPickers";
import { ConfirmationMiscEntryRows } from "./ConfirmationMiscEntryRows";
import type { ConfirmationEntryProps } from "./types";
import "./entryDrawer.css";
import "./miscEntryDrawer.css";
type MiscInput = {
    text: string;
    range: string;
    provider: EntryProvider | null;
    settlement: Pick<FulfillmentSupplierOption, "id" | "name"> | null;
};
type EntryIssue = EntryParseResult["errors"][number];
type PreviewSelectionIdentity = { id: string; raw: string; identity: string };
type CategoryDraft = {
    existing: EntryExistingDraft[];
    input: MiscInput;
    initialText?: string;
    previewPatches: Record<string, Partial<FulfillmentOrderLine>>;
    selectionIdentities?: PreviewSelectionIdentity[];
    subjectIdentities?: ConfirmationEntryIdentity[];
    unselectedPreviewIds?: string[];
    original?: {
        seed: ExistingConfirmationEntry;
        initialInput: MiscInput;
    };
};
const isDirty = (draft?: CategoryDraft) => Boolean(draft && ((draft.original ? JSON.stringify(draft.input) !== JSON.stringify(draft.original.initialInput) : draft.input.text.trim() !== (draft.initialText || "").trim()) || draft.existing.some(row => row.removed || Object.keys(changedEntryFields(row)).length)));
const subjectIdentity = ({ patch }: EntryParseResult["items"][number]) => JSON.stringify([patch.itemNameSnapshot, patch.travelerRole, patch.unit, patch.itemSpecSnapshot, patch.dayNumbers, patch.serviceDates]);
const issueControl = (issue: EntryIssue): string => {
    if (issue.field === "range")
        return "confirmation-entry-text";
    if (issue.field === "provider")
        return "服务主体";
    return "confirmation-entry-text";
};
const existingAmount = (row: EntryExistingDraft) => {
    if (row.removed)
        return "0";
    const patch = changedEntryFields(row);
    if (!["quantity", "billingCount", "unitPriceAmount"].some((field) => field in patch))
        return row.base.amount;
    const line = { ...row.base, ...patch };
    try {
        return calculateFulfillmentLineAmount(line.quantity, line.billingCount, line.unitPriceAmount);
    }
    catch {
        return "0";
    }
};
/** 仅在全部载入费用结算一致时沿用共同值，混合主体不取任意首行默认值。 */
function commonSettlement(rows: EntryExistingDraft[]): MiscInput["settlement"] {
    const first = rows[0]?.base;
    if (!first?.settlementSupplierId || !first.settlementSupplierName) return null;
    return rows.every(({ base }) => base.settlementSupplierId === first.settlementSupplierId && base.settlementSupplierName === first.settlementSupplierName)
        ? { id: first.settlementSupplierId, name: first.settlementSupplierName } : null;
}
const example = "快递费 100\n运营费 学生20\n保险费 52人×12\n资料服务 老师 2人×3天×50";
const billingExamples = [
    ["整项金额", "快递费 100元"],
    ["随统一人数", "运营费 学生20元"],
    ["指定实际人数", "保险费 45×12"],
    ["人数 × 天数", "资料服务 老师3天×50\n资料服务 2人×3天×50"],
] as const;

/** 勾选优先跟随原文，再按项目与计费语义匹配；量价和调序不覆盖人工取消。 */
function reconcilePreviewSelection(items: EntryParseResult["items"], previous: PreviewSelectionIdentity[]): PreviewSelectionIdentity[] {
    const used = new Set<string>();
    const matched = items.map(item => {
        const found = previous.find(record => record.raw === item.raw && !used.has(record.id));
        if (found) used.add(found.id);
        return found;
    });
    return items.map((item, index) => {
        const identity = JSON.stringify([item.patch.itemNameSnapshot, item.patch.travelerRole, item.patch.unit, item.patch.itemSpecSnapshot]);
        const old = matched[index] || previous.find(record => record.identity === identity && !used.has(record.id));
        if (old) used.add(old.id);
        return { id: old?.id || crypto.randomUUID(), raw: item.raw, identity };
    });
}

/** 暂存待设置主体，明确应用后才修改当前选中费用；选择范围变化时由 key 重建。 */
function MiscSubjectSettings({ runtime, count, disabled, settlementLocked, original, onApply }: {
    runtime: ConfirmationEntryProps["runtime"];
    count: number;
    disabled: boolean;
    settlementLocked: boolean;
    original: boolean;
    onApply: (provider: FulfillmentSupplierOption | null | undefined, settlement: FulfillmentSupplierOption | null | undefined) => void;
}) {
    const [provider, setProvider] = useState<FulfillmentSupplierOption | null>();
    const [settlement, setSettlement] = useState<FulfillmentSupplierOption | null>();
    const unavailable = disabled || !count;
    return <details className="confirmation-misc-subjects" open>
      <summary>主体设置 <span>{original ? "当前原行" : count ? `已选 ${count} 项费用` : "勾选费用后设置"}</span></summary>
      <div className="confirmation-misc-subject-fields">
        <EntrySupplierPicker label="服务主体" value={provider === undefined ? "保持原值" : provider?.name || "待设置"} selectedKey={provider?.id} options={runtime.quickEntryFulfillmentSupplierOptions.filter(option => option.canBeFulfillmentSubject)} onSearch={runtime.searchQuickEntryFulfillmentSuppliers} onChoose={setProvider} loading={runtime.quickEntrySupplierOptionsLoading} error={runtime.quickEntrySupplierOptionsError} disabled={unavailable}/>
        <EntrySupplierPicker label="结算主体" value={settlement === undefined ? "保持原值" : settlement?.name || "待设置"} selectedKey={settlement?.id} options={runtime.settlementSupplierOptions.filter(option => option.canBeSettlementSubject)} onSearch={runtime.searchSettlementSuppliers} onChoose={setSettlement} loading={runtime.settlementSupplierOptionsLoading} error={runtime.settlementSupplierOptionsError} disabled={unavailable || settlementLocked}/>
        <ConfirmationButton size="sm" disabled={unavailable || (provider === undefined && settlement === undefined)} onClick={() => {
            onApply(provider, settlement);
            setProvider(undefined); setSettlement(undefined);
        }}>设置所选</ConfirmationButton>
      </div>
      <p>杂费服务主体选填，可保持未设置。结算主体可在此设置，也可返回全部费用后统一设置。</p>
      {settlementLocked ? <p>已归口明细的结算主体由原协议决定，请取消勾选这些行后设置其他明细。</p> : null}
    </details>;
}
/** 杂费独立录入：本类草稿常驻，已有行使用稳定 ID 补丁，新增经正式校验后交给父级应用。 */
export function ConfirmationMiscEntryDrawer(props: Omit<ConfirmationEntryProps, "category">) {
    const { open, runtime, editableIds, disabled, onClose, onDirtyChange, onApply, newOnly, existingLineId } = props;
    const [drafts, setDrafts] = useState<Record<string, CategoryDraft>>({});
    const [error, setError] = useState("");
    const [applying, setApplying] = useState(false);
    const [existingSelected, setExistingSelected] = useState<Set<string>>(new Set());
    const allSelectionRef = useRef<HTMLInputElement>(null);
    const subjectSelectionRef = useRef<object | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const layoutRef = useRef<HTMLDivElement>(null);
    const issueId = useId();
    const previewRef = useRef<HTMLDivElement>(null);
    const openRef = useRef("");
    const workspaceId = runtime.editor?.workspace.id || "";
    const selectedIds = runtime.lines.filter(line => editableIds.has(line.id) && confirmationCategoryForLine(line) === "misc" && runtime.selectedIds.has(line.id)).map(line => line.id).sort();
    const contextKey = `${workspaceId}:misc:${existingLineId ? `line:${existingLineId}` : newOnly ? "new" : "edit"}`;
    const requestedKey = `${workspaceId}:misc:${existingLineId ? `line:${existingLineId}` : newOnly ? "new" : selectedIds.length ? `selected:${JSON.stringify(selectedIds)}` : "edit"}`;
    const scopeIdentity = useRef({ contextKey, key: requestedKey });
    // 打开期间保持当前草稿，不能因原行失去权限而改为编辑其他未选费用。
    if (!open || !openRef.current || scopeIdentity.current.contextKey !== contextKey) scopeIdentity.current = { contextKey, key: requestedKey };
    const key = scopeIdentity.current.key;
    const draft = drafts[key];
    const input = draft?.input;
    const routeDays = runtime.editor?.workspace.routeDays || [];
    const locked = disabled || applying;
    useEffect(() => {
        if (!open) {
            openRef.current = "";
            return;
        }
        const session = `${key}:${newOnly ? "new" : "edit"}`;
        if (openRef.current === session)
            return;
        openRef.current = session;
        setError("");
        runtime.setQuickEntryResourceType(ENTRY_RESOURCE_TYPES["misc"]);
        setDrafts((previous) => {
            if (isDirty(previous[key]))
                return previous;
            const available = runtime.lines.filter((line) => editableIds.has(line.id) && confirmationCategoryForLine(line) === "misc");
            if (existingLineId) {
                const line = available.find(item => item.id === existingLineId);
                if (!line)
                    return previous;
                const seed = prepareExistingMiscEntry(line, routeDays);
                const initialInput: MiscInput = { text: seed.input.text, range: seed.input.range, provider: seed.input.provider || null, settlement: seed.input.settlement || null };
                return { ...previous, [key]: { existing: [{ base: seed.base, patch: {}, removed: false }], input: initialInput, previewPatches: {}, original: { seed, initialInput } } };
            }
            const selected = available.filter((line) => runtime.selectedIds.has(line.id));
            const existing = newOnly ? [] : (selected.length ? selected : available).map((base) => ({ base: structuredClone(base), patch: {}, removed: false }));
            const initialText = !newOnly ? existing.map(row => prepareExistingMiscEntry(row.base, routeDays).input.text).join("\n") : undefined;
            const input: MiscInput = { text: initialText || "", range: routeDays.map(day => day.dayNo).join(","), provider: null, settlement: commonSettlement(existing) };
            return { ...previous, [key]: { existing, input, initialText, previewPatches: {} } };
        });
    }, [open, key, "misc", newOnly, existingLineId, runtime, editableIds, draft]);
    useEffect(() => { onDirtyChange(Object.entries(drafts).some(([draftKey, value]) => draftKey.startsWith(`${workspaceId}:`) && isDirty(value))); }, [drafts, workspaceId, onDirtyChange]);
    useEffect(() => {
        if (!open || !existingLineId || !draft?.original)
            return;
        const frame = requestAnimationFrame(() => textareaRef.current?.focus({ preventScroll: true }));
        return () => cancelAnimationFrame(frame);
    }, [open, key, existingLineId, Boolean(draft?.original)]);
    const people = runtime.quickEntryContext?.peopleCounts ?? runtime.editor?.workspace.peopleCounts;
    const roleCounts = useMemo(() => people ? { 学生: people.studentCount, 老师: people.teacherCount, 导游: people.guideCount, 司机: people.driverCount } : {}, [people]);
    const originalResult = draft?.original && input ? parseExistingMiscEntry(draft.original.seed, { ...input, candidates: [], routeDays, roleCounts }) : null;
    const selectedResult = input && draft?.initialText !== undefined ? parseSelectedMiscDraft({ ...input, candidates: [], routeDays, roleCounts }, draft.existing.map(row => row.base)) : null;
    const displayedExisting = draft?.existing.map(row => {
        const parsed = selectedResult?.drafts.find(item => item.base.id === row.base.id);
        return { ...row, removed: row.removed || Boolean(parsed?.removed), patch: { ...parsed?.patch, ...(draft.original ? originalResult?.patch : {}), ...row.patch } };
    }) || [];
    const parsed: EntryParseResult = !input ? { items: [], errors: [] } : originalResult
        ? { items: [], errors: originalResult.errors } : selectedResult || parseMiscEntry({ ...input, candidates: [], routeDays, roleCounts });
    const { identities: subjectIdentities, ambiguous } = reconcileConfirmationEntryIdentities(parsed.items, draft?.subjectIdentities || [], subjectIdentity, input?.text || "", parsed.errors.length > 0, id => Boolean(draft?.previewPatches[`${key}:${id}`]));
    const previewItems = parsed.items.map((entry, index) => {
        const previewKey = `${key}:${subjectIdentities[index].id}`;
        const selectionId = draft?.selectionIdentities?.[index]?.id || previewKey;
        return { ...entry, previewKey, selectionId, patch: { ...entry.patch, ...draft?.previewPatches[previewKey] } };
    });
    const previewSelected = new Set(previewItems.filter(item => !draft?.unselectedPreviewIds?.includes(item.selectionId)).map(item => item.previewKey));
    const previewErrors = [...parsed.errors, ...(ambiguous ? [{ message: CONFIRMATION_ENTRY_IDENTITY_ERROR, raw: input?.text || "", sourceStart: 0, sourceEnd: input?.text.length || 0, field: "formula" as const }] : [])];
    const total = sumFulfillmentAmounts(previewItems.map((item) => item.patch.amount || "0"));
    const allTotal = sumFulfillmentAmounts([total, ...displayedExisting.map(existingAmount)]);
    const selectedPreviews = previewItems.filter((item) => previewSelected.has(item.previewKey));
    const updateDraft = (transform: (value: CategoryDraft) => CategoryDraft) => setDrafts((previous) => previous[key] ? { ...previous, [key]: transform(previous[key]) } : previous);
    const updateInput = (patch: Partial<MiscInput>) => updateDraft(value => {
        const input = { ...value.input, ...patch };
        if (patch.text === undefined || value.original) return { ...value, input };
        const parsed = value.initialText !== undefined ? parseSelectedMiscDraft({ ...input, candidates: [], routeDays, roleCounts }, value.existing.map(row => row.base)) : parseMiscEntry({ ...input, candidates: [], routeDays, roleCounts });
        const matched = reconcilePreviewSelection(parsed.items, value.selectionIdentities || []);
        const selectionIdentities = parsed.errors.length
            ? [...matched, ...(value.selectionIdentities || []).filter(record => !matched.some(item => item.id === record.id))]
            : matched;
        const { identities: subjectIdentities, ambiguous } = reconcileConfirmationEntryIdentities(parsed.items, value.subjectIdentities || [], subjectIdentity, input.text, parsed.errors.length > 0, id => Boolean(value.previewPatches[`${key}:${id}`]));
        const retained = new Set(subjectIdentities.map(item => `${key}:${item.id}`));
        return { ...value, input, selectionIdentities, subjectIdentities: ambiguous ? value.subjectIdentities : subjectIdentities, previewPatches: Object.fromEntries(Object.entries(value.previewPatches).filter(([id]) => ambiguous || retained.has(id))), unselectedPreviewIds: value.unselectedPreviewIds?.filter(id => selectionIdentities.some(record => record.id === id)) };
    });
    const selectPreviews = (ids: string[], selected: boolean) => {
        if (locked) return;
        updateDraft(value => {
            const unselected = new Set(value.unselectedPreviewIds);
            for (const id of ids) {
                if (selected) unselected.delete(id);
                else unselected.add(id);
            }
            return { ...value, unselectedPreviewIds: [...unselected] };
        });
    };
    const followPreview = (input: HTMLTextAreaElement, sourceStart?: number) => {
        const scroll = previewRef.current;
        if (!scroll) return;
        const candidates = [...scroll.querySelectorAll<HTMLElement>("[data-preview-start]")];
        const caret = sourceStart ?? (input.selectionDirection === "backward" ? input.selectionStart : input.selectionEnd);
        const distance = (row: HTMLElement) => Math.max(Number(row.dataset.previewStart) - caret, caret - Number(row.dataset.previewEnd), 0);
        const target = candidates.reduce<HTMLElement | null>((best, row) => !best || distance(row) < distance(best) ? row : best, null);
        scroll.querySelectorAll<HTMLElement>("[data-preview-current]").forEach((row) => row.removeAttribute("data-preview-current"));
        if (!target)
            return;
        target.dataset.previewCurrent = "true";
        const bounds = scroll.getBoundingClientRect(), rowBounds = target.getBoundingClientRect();
        const top = bounds.top + (scroll.querySelector("thead")?.getBoundingClientRect().height || 0) + 8;
        const bottom = bounds.bottom - 8;
        if (scroll.clientHeight > 0) {
            if (rowBounds.top < top)
                scroll.scrollTop += rowBounds.top - top;
            else if (rowBounds.bottom > bottom)
                scroll.scrollTop += rowBounds.bottom - bottom;
        }
    };
    const locate = (contextKey: string, start: number, end: number, issue?: EntryIssue) => {
        if (contextKey !== key) return;
        const targetLabel = issue ? issueControl(issue) : "confirmation-entry-text";
        requestAnimationFrame(() => {
            const input = targetLabel === "confirmation-entry-text" ? textareaRef.current : layoutRef.current?.querySelector<HTMLInputElement>(`input[aria-label="${targetLabel}"]`);
            if (!input)
                return;
            const details = input.closest("details");
            if (details)
                details.open = true;
            input.focus({ preventScroll: true });
            if (input instanceof HTMLTextAreaElement) {
                input.setSelectionRange(start, end);
                const height = Number.parseFloat(getComputedStyle(input).lineHeight) || 26;
                input.scrollTop = Math.max(0, (input.value.slice(0, start).split("\n").length - 1) * height - input.clientHeight / 2);
            }
            // 上下布局由抽屉内容区滚动；只把被遮挡的输入框带回该容器，不移动页面背景。
            const sheetBody = input.closest<HTMLElement>(".confirmation-sheet-body");
            if (sheetBody && sheetBody.clientHeight > 0) {
                const bounds = sheetBody.getBoundingClientRect(), inputBounds = input.getBoundingClientRect();
                if (inputBounds.top < bounds.top + 8 || inputBounds.bottom > bounds.bottom - 8) {
                    sheetBody.scrollTop += inputBounds.top - bounds.top - 8;
                }
            }
            if (textareaRef.current)
                followPreview(textareaRef.current, start);
        });
    };
    const apply = async () => {
        if (!draft || locked || previewErrors.length)
            return;
        setError("");
        try {
            if (draft.existing.some((row) => !editableIds.has(row.base.id)))
                throw new Error("部分明细已不可编辑，草稿已保留，请核对当前状态");
            const change = buildConfirmationEntryChange(displayedExisting, runtime.lines, previewItems.map((item) => item.patch));
            if (!change.additions.length && !change.updates.length && !change.removals.length) {
                onClose();
                return;
            }
            setApplying(true);
            await onApply(change);
            setDrafts((previous) => { const next = { ...previous }; delete next[key]; return next; });
            onClose();
        }
        catch (cause) {
            setError(cause instanceof Error ? cause.message : "应用失败，草稿已保留");
        }
        finally {
            setApplying(false);
        }
    };
    const selectableExisting = displayedExisting.filter((row) => !row.removed);
    const selectedCount = selectableExisting.filter((row) => draft?.original || existingSelected.has(row.base.id)).length + selectedPreviews.length;
    const selectableCount = selectableExisting.length + previewItems.length;
    const subjectRows = selectableExisting.filter(row => draft?.original || existingSelected.has(row.base.id));
    const subjectCount = subjectRows.length + selectedPreviews.length;
    const settlementLocked = subjectRows.some(row => row.base.fulfillmentOrderId);
    const subjectSelectionKey = JSON.stringify([key, open, Boolean(locked), settlementLocked, subjectRows.map(row => row.base.id).sort(), selectedPreviews.map(item => item.previewKey).sort()]);
    const subjectSelection = useMemo(() => ({}), [subjectSelectionKey]);
    subjectSelectionRef.current = subjectSelection;
    const applySubjects = (provider: FulfillmentSupplierOption | null | undefined, settlement: FulfillmentSupplierOption | null | undefined) => {
        if (locked || !subjectCount || subjectSelectionRef.current !== subjectSelection || (settlement !== undefined && settlementLocked)) return;
        if (ambiguous) { setError(CONFIRMATION_ENTRY_IDENTITY_ERROR); return; }
        if (draft?.original) {
            updateInput({ ...(provider !== undefined ? { provider } : {}), ...(settlement !== undefined ? { settlement } : {}) });
            return;
        }
        const patch: Partial<FulfillmentOrderLine> = {
            ...(provider !== undefined ? { fulfillmentSupplierId: provider?.id ?? null, fulfillmentSupplierName: provider?.name ?? null } : {}),
            ...(settlement !== undefined ? { settlementSupplierId: settlement?.id ?? null, settlementSupplierName: settlement?.name ?? null } : {}),
        };
        updateDraft(value => ({ ...value,
            subjectIdentities,
            existing: value.existing.map(row => subjectRows.some(selected => selected.base.id === row.base.id) ? { ...row, patch: { ...row.patch, ...patch } } : row),
            previewPatches: { ...value.previewPatches, ...Object.fromEntries(selectedPreviews.map(item => [item.previewKey, { ...value.previewPatches[item.previewKey], ...patch }])) },
        }));
    };
    useEffect(() => {
        if (allSelectionRef.current)
            allSelectionRef.current.indeterminate = selectedCount > 0 && selectedCount < selectableCount;
    }, [selectedCount, selectableCount]);
    const textIssue = previewErrors.find(issue => issueControl(issue) === "confirmation-entry-text")?.message;
    const changeCount = displayedExisting.filter(row => row.removed || Object.keys(changedEntryFields(row)).length).length;
    return <ConfirmationSheet open={open} onClose={onClose} title={`${"\u6742\u8D39"} · 录入与修改`} className={`confirmation-entry-sheet confirmation-entry-${"misc"}`} description={draft?.original ? `正在编辑第 ${draft.original.seed.base.lineNo} 行：左侧修改原费用，右侧核对；应用后仍需保存。` : runtime.editor?.workspace.status === "draft" ? "左侧录入，右侧逐项核对；关闭保留草稿，应用后仍需保存。" : "左侧录入，右侧逐项核对；关闭保留草稿，应用后直接保存新增费用。"} footer={<><span className="confirmation-entry-footer-status">{changeCount ? `修改 ${changeCount} 行 · ` : ""}{previewItems.length} 条新增{previewErrors.length ? ` · ${previewErrors.length} 处待修正` : isDirty(draft) ? " · 草稿已保留" : " · 尚未修改"}</span><div className="confirmation-entry-actions">
      <ConfirmationButton variant="ghost" disabled={locked} onClick={() => { setDrafts((previous) => { const next = { ...previous }; delete next[key]; return next; }); openRef.current = ""; }}>重置</ConfirmationButton>
      <ConfirmationButton disabled={locked} onClick={onClose}>{isDirty(draft) ? "保留草稿并关闭" : "关闭"}</ConfirmationButton>
      <ConfirmationButton variant="default" loading={applying} disabled={locked || !!previewErrors.length || !isDirty(draft)} onClick={() => void apply()}>应用到确认行池</ConfirmationButton>
    </div></>}>
    {draft && input ? <div ref={layoutRef} className="confirmation-entry-layout" data-category={"misc"}>
      <div className="confirmation-entry-context confirmation-misc-context"><span>{people ? Object.entries(roleCounts).map(([role, count]) => <span key={role}>{role}{count ?? "待确认"}{count == null ? "" : "人"}</span>) : "人数未确认，请明确填写"}</span><span>行程{routeDays.length}天 · 服务天数可按项调整</span></div>
      <div className="confirmation-entry-columns">
        <section className="confirmation-entry-compose"><header><strong>费用录入</strong><label htmlFor="confirmation-entry-text">一行一项 · 金额单位：元</label></header>
          {<textarea ref={textareaRef} id="confirmation-entry-text" aria-invalid={!!textIssue} aria-describedby={textIssue ? `${issueId}-text` : undefined} aria-label="杂费录入文本" value={input.text} placeholder={example} disabled={locked} spellCheck={false} onChange={(event) => {
                    updateInput({ text: event.target.value });
                    requestAnimationFrame(() => {
                        if (textareaRef.current)
                            followPreview(textareaRef.current);
                    });
                }} onSelect={(event) => followPreview(event.currentTarget)} onKeyUp={(event) => followPreview(event.currentTarget)} onClick={(event) => followPreview(event.currentTarget)}/>}
          {textIssue ? <p id={`${issueId}-text`} className="confirmation-entry-field-hint">{textIssue}</p> : null}
          <section className="confirmation-misc-guide" aria-label="计费方式示例"><dl>{billingExamples.map(([label, example]) => <div key={label}><dt>{label}</dt><dd>{example}</dd></div>)}</dl><p>支持四种计费方式。角色后单价随统一人数；两个数字为实际人数×单价；三个数字为人数×天数×单价。乘号可用 x、×、*，单位可省略，按角色天数须保留“天”。</p></section>
          {draft.original ? <details className="confirmation-entry-rules"><summary>原行补充信息</summary><p>文本未表达的原值继续保留，可按需修改说明与计价单位。</p>{([['itemSpecSnapshot', '服务规格'], ['unit', '数量单位'], ['remark', '备注']] as const).map(([field, label]) => <label key={field}>{label}<ConfirmationInput aria-label={`原行${label}`} value={draft.existing[0].patch[field] ?? displayedExisting[0]?.patch[field] ?? draft.existing[0].base[field] ?? ''} disabled={locked} onChange={event => updateDraft(value => ({ ...value, existing: value.existing.map(row => ({ ...row, patch: { ...row.patch, [field]: event.target.value } })) }))}/></label>)}<label>计价方式<select aria-label="原行计价方式" value={draft.existing[0].patch.pricingUnitType ?? draft.existing[0].base.pricingUnitType} disabled={locked} onChange={event => updateDraft(value => ({ ...value, existing: value.existing.map(row => ({ ...row, patch: { ...row.patch, pricingUnitType: event.target.value } })) }))}>{FULFILLMENT_PRICING_UNIT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}{!FULFILLMENT_PRICING_UNIT_OPTIONS.some(option => option.value === draft.existing[0].base.pricingUnitType) ? <option value={draft.existing[0].base.pricingUnitType}>{pricingUnitTypeLabel(draft.existing[0].base.pricingUnitType)}</option> : null}</select></label></details> : null}
        </section>
        <aside className="confirmation-entry-preview"><header><strong>{"实时预览"}</strong><span>{draft.existing.length + previewItems.length} 项费用</span></header>
          <MiscSubjectSettings key={subjectSelectionKey} runtime={runtime} count={subjectCount} disabled={Boolean(locked)} settlementLocked={settlementLocked} original={Boolean(draft.original)} onApply={applySubjects}/>
          <div className="confirmation-misc-preview-tools"><label><input ref={allSelectionRef} type="checkbox" aria-label="选择全部可编辑费用" aria-checked={selectedCount > 0 && selectedCount < selectableCount ? "mixed" : selectedCount > 0 && selectedCount === selectableCount} checked={selectableCount > 0 && selectedCount === selectableCount} disabled={locked || !!draft.original || !selectableCount} onChange={(event) => { setExistingSelected(event.target.checked ? new Set(selectableExisting.map(row => row.base.id)) : new Set()); selectPreviews(previewItems.map(item => item.selectionId), event.target.checked); }}/>全选费用</label><span>点击项目或公式可定位原文</span></div>
          <div className="confirmation-entry-preview-scroll" ref={previewRef}><table className="confirmation-entry-preview-table" aria-label="本类已有明细编辑">
            <ConfirmationMiscEntryRows rows={displayedExisting} newRows={previewItems} issues={previewErrors} locations={selectedResult?.locations} sourceText={input.text} original={Boolean(draft.original)} groupId={key} active runtime={runtime} disabled={locked} selectedNew={previewSelected} selectedExisting={existingSelected} onSelectExisting={(ids, selected) => {
                    if (locked)
                        return;
                    setExistingSelected((previous) => {
                        const next = new Set(previous);
                        for (const id of ids) {
                            if (selected)
                                next.add(id);
                            else
                                next.delete(id);
                        }
                        return next;
                    });
                }} onSelectNew={(itemKey, selected) => selectPreviews(previewItems.filter(item => item.previewKey === itemKey).map(item => item.selectionId), selected)} onLocate={locate} onChange={existing => updateDraft(value => ({ ...value, existing }))}/>
          </table></div>
          <div className="confirmation-entry-summary"><span>{"\u6742\u8D39"}合计</span><strong>{formatConfirmationAmount(allTotal)}</strong></div>
          <div className="confirmation-entry-checks">{previewErrors.length ? <><button type="button" onClick={() => {
                const issue = previewErrors[0];
                locate(key, issue.sourceStart, issue.sourceEnd, issue);
            }}>{previewErrors.length} 处待修正，点击定位</button><ul className="confirmation-entry-issue-list">{previewErrors.map(issue => <li key={issue.sourceStart}><button type="button" onClick={() => locate(key, issue.sourceStart, issue.sourceEnd, issue)}>{issue.message}<strong>去修正</strong></button></li>)}</ul></> : <span>已识别费用按原计费规则核对；勾选费用后可设置主体。</span>}</div>
        </aside>
      </div>
      {error ? <div role="alert" className="confirmation-entry-error">{error}</div> : null}
    </div> : null}
  </ConfirmationSheet>;
}
